import type { TokenCredential } from "@azure/identity";
import { LocalCliError } from "./errors.js";

export interface AuthedJsonArgs {
  credential: TokenCredential;
  scope: string;
  method: "GET" | "POST" | "PUT" | "DELETE";
  url: string;
  body?: unknown;
  /** HTTP statuses to treat as success-with-undefined instead of throwing (e.g. 404 for existence checks). */
  okStatuses?: number[];
}

/** Result wrapper exposing the status for callers that branch on 404 etc. */
export interface AuthedJsonResult<T> { status: number; data: T | undefined }

export async function authedJsonResult<T = unknown>(args: AuthedJsonArgs): Promise<AuthedJsonResult<T>> {
  const token = await args.credential.getToken(args.scope);
  if (!token) {
    throw new LocalCliError({
      code: "AUTH_NULL_TOKEN",
      message: `Azure returned no token for scope ${args.scope}.`,
      hint: "Run 'az login' and retry.",
    });
  }
  const res = await fetch(args.url, {
    method: args.method,
    headers: {
      Authorization: `Bearer ${token.token}`,
      ...(args.body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    ...(args.body !== undefined ? { body: JSON.stringify(args.body) } : {}),
  });

  const ok = res.ok || (args.okStatuses?.includes(res.status) ?? false);
  if (!ok) {
    const text = await res.text().catch(() => "");
    throw new LocalCliError({
      code: "AZURE_HTTP_ERROR",
      message: `${args.method} ${args.url} failed: ${res.status.toString()} ${res.statusText}${text ? ` — ${text.slice(0, 400)}` : ""}`,
    });
  }

  const raw = await res.text();
  const data = raw ? (JSON.parse(raw) as T) : undefined;
  return { status: res.status, data };
}

/** Convenience: returns the parsed body (undefined for empty), throwing on non-2xx. */
export async function authedJson<T = unknown>(args: AuthedJsonArgs): Promise<T | undefined> {
  return (await authedJsonResult<T>(args)).data;
}

/** One page of an ARM list response. */
interface ArmPage<T> {
  value?: T[];
  nextLink?: string;
}

export interface AuthedJsonPagedArgs {
  credential: TokenCredential;
  scope: string;
  url: string;
  /** Safety bound: stop after this many pages rather than trust a server's token forever. */
  maxPages?: number;
}

/**
 * GET an ARM **list** endpoint and return every item across every page.
 *
 * USE THIS FOR ANY ARM LIST. `authedJson` reads a single response, which is a
 * silent correctness bug against a paginated endpoint: ARM splits list results
 * with `nextLink`, and it is under no obligation to put items on the first page.
 * An EMPTY page carrying a continuation token is legal — and real. A caller that
 * reads only page 1 sees zero items and concludes the resources do not exist.
 *
 * Observed live: a subscription-wide Microsoft.CognitiveServices/accounts list
 * returned pages 1-2 empty (each with a $skiptoken) and all three AIServices
 * accounts on page 3, deterministically, at both subscription and RG scope. The
 * un-paginated read left `resolveFoundryProject` blind, so every Foundry endpoint
 * was rejected — including a project created seconds earlier — breaking the
 * installer's deploy-advisors phase and ~20 CLI commands against a live platform.
 * No mocked-ARM unit test produces an empty first page; only a real subscription
 * does. Hence this helper, and hence its "empty page is not the end" test.
 *
 * The page walk is BOUNDED (`maxPages`, default 100): a server that always
 * returns a continuation token must not hang the CLI. Each page is authorized
 * afresh — a continuation URL is still an ARM call — and a non-2xx on any page
 * throws rather than silently truncating the list, because a short list here
 * reads as "the resource does not exist" and sends the user off to create a
 * duplicate.
 */
export async function authedJsonPagedValues<T = unknown>(args: AuthedJsonPagedArgs): Promise<T[]> {
  const maxPages = args.maxPages ?? 100;
  const items: T[] = [];
  let url: string | undefined = args.url;

  for (let page = 0; url && page < maxPages; page++) {
    const body: ArmPage<T> | undefined = await authedJson<ArmPage<T>>({
      credential: args.credential,
      scope: args.scope,
      method: "GET",
      url,
    });
    items.push(...(body?.value ?? []));
    // An empty `value` is NOT a terminator — only the absence of nextLink is.
    url = body?.nextLink;
  }

  return items;
}
